/* global policies displayApprovalStatuses */

import React, { useState, useEffect, useRef, Fragment } from 'react';

import { Modal } from 'flowbite';
import moment from 'moment';

import PurchaseRequisitionsEditModal from './PurchaseRequisitionsEditModal';
import PurchaseRequisitionsViewModal from './PurchaseRequisitionsViewModal';

import { formatFigure } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import useApiRequest from 'Hooks/useApiRequest';
import usePagination from 'Hooks/usePagination';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function PurchaseRequisitionsIndexTable(props) {
    const [updatedPR, setUpdatedPR] = useState({});
    const [deletedPR, setDeletedPR] = useState({});

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [purchaseRequisitionsViewModalElement, setPurchaseRequisitionsViewModalElement] = useState(null);
    const [purchaseRequisitionsEditModalElement, setPurchaseRequisitionsEditModalElement] = useState(null);
    const [purchaseRequisitionsDeleteModalElement, setPurchaseRequisitionsDeleteModalElement] = useState(null);

    const [filters, setFilters] = useState({ search: '' });

    const purchaseRequisitionsViewModalRef = useRef();
    const purchaseRequisitionsEditModalRef = useRef();
    const purchaseRequisitionsDeleteModalRef = useRef();

    const pagination = usePagination({ perPage: 25 });

    const fetchPurchaseRequisitionsRequest = useApiRequest({ url: props.indexUrl });

    const [viewedPR, setViewedPR] = useState({});
    useEffect(() => {
        if (Object.keys(viewedPR).length > 0) {
            purchaseRequisitionsViewModalElement && purchaseRequisitionsViewModalElement.show();
        }
    }, [viewedPR]);
    const viewPR = (pr) => {
        fetchPrDetailed(pr);
    };

    useEffect(() => {
        purchaseRequisitionsViewModalRef && setPurchaseRequisitionsViewModalElement(new Modal(purchaseRequisitionsViewModalRef.current, modalOptions(false)));
        purchaseRequisitionsEditModalRef && setPurchaseRequisitionsEditModalElement(new Modal(purchaseRequisitionsEditModalRef.current, modalOptions(false)));
        purchaseRequisitionsDeleteModalRef &&
            setPurchaseRequisitionsDeleteModalElement(new Modal(purchaseRequisitionsDeleteModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        fetchPurchaseRequisitionsRequest.get({ per_page: pagination.perPage, page: pagination.page, search: filters.search });
    }, [pagination.page, pagination.perPage, filters.search]);

    useEffect(() => {
        if (props.refetch) {
            pagination.setPage(1);
        }
    }, [props.refetch]);

    useEffect(() => {
        if (Object.keys(updatedPR).length > 0) {
            purchaseRequisitionsEditModalElement && purchaseRequisitionsEditModalElement.show();
        }
    }, [updatedPR]);

    useEffect(() => {
        if (Object.keys(deletedPR).length > 0) {
            purchaseRequisitionsDeleteModalElement && purchaseRequisitionsDeleteModalElement.show();
        }
    }, [deletedPR]);

    useEffect(() => {
        if (fetchPurchaseRequisitionsRequest.data) {
            const { meta } = fetchPurchaseRequisitionsRequest.data;
            pagination.setTotal(meta.total);
            pagination.setLastPage(meta.last_page);
            pagination.setFrom(meta.from);
            pagination.setTo(meta.to);
        }
    }, [fetchPurchaseRequisitionsRequest.data]);

    const fetchPrDetailed = (pr) => {
        axios
            .untranslated()
            .get(pr.detailedUrl)
            .then(({ data }) => {
                setViewedPR({ ...data.pr, approval_history: data.approval_history || data.approvers || [] });
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const fetchPrEdit = (pr) => {
        axios
            .untranslated()
            .get(pr.editUrl)
            .then(({ data }) => {
                setUpdatedPR({ ...data.pr });
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const editPR = (pr) => {
        fetchPrEdit(pr);
    };

    const deletePR = (pr) => {
        setDeletedPR({ ...pr });
    };

    const successHandler = () => {
        setUpdatedPR({});
        setDeletedPR({});
        props.refetchAllHandler();
        purchaseRequisitionsEditModalElement && purchaseRequisitionsEditModalElement.hide();
        purchaseRequisitionsDeleteModalElement && purchaseRequisitionsDeleteModalElement.hide();
        setViewedPR({});
        purchaseRequisitionsViewModalElement && purchaseRequisitionsViewModalElement.hide();
    };

    const closeModal = () => {
        setUpdatedPR({});
        setDeletedPR({});
        purchaseRequisitionsEditModalElement && purchaseRequisitionsEditModalElement.hide();
        purchaseRequisitionsDeleteModalElement && purchaseRequisitionsDeleteModalElement.hide();
        setViewedPR({});
        purchaseRequisitionsViewModalElement && purchaseRequisitionsViewModalElement.hide();
    };

    const createPurchaseOrder = (id) => {
        if (!props.createPurchaseOrderUrl) {
            return;
        }
        const url = props.createPurchaseOrderUrl.replace('REQUISITION_ID', id);
        window.location.href = url;
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeletePR = () => {
        setActionInProgress(true);
        axios
            .delete(props.deleteUrl.replace('REQUISITION_ID', deletedPR?.id), { data: {} })
            .then((response) => {
                setActionCompleted(true);
            })
            .catch((e) => {
                setActionFailed(true);
            });
    };

    return (
        <div>
            <div className="tw-flex tw-justify-between tw-mb-4">
                <div>
                    <h4 className="table__header">{props.heading}</h4>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            forcePage={pagination.page - 1}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2">
                    <div>
                        <PerPageSelect
                            value={pagination.perPage}
                            onChange={(e) => {
                                pagination.setPerPage(parseInt(e.target.value));
                            }}
                        />
                    </div>
                    <div>
                        <TableSearchInput searchValue={filters.search} handleEnterPress={(e, value) => setFilters((prev) => ({ ...prev, search: value }))} />
                    </div>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table pr-table">
                    <thead>
                        <tr className="*:tw-font-semibold *:tw-bg-blue-25">
                            <th className="tw-min-w-64 tw-w-full tw-sticky tw-left-0 tw-h-fit">
                                <span className="tw-block">{$t('Procurement Name')}</span>
                            </th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Unique ID')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Owner')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Value')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Procurement method')}</th>
                            <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Target Award')}</th>
                            {props.type === 'opened' ? (
                                <>
                                    <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Opened By')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Opened At')}</th>
                                </>
                            ) : (
                                <>
                                    <th className="tw-w-44 tw-min-w-44 tw-max-w-44">
                                        <Tooltip
                                            placement="right-end"
                                            allowTooltip={props.type === 'approved'}
                                            tooltipContent={$t("When a user has manual approvals for PRs turned off, 'n/a' will be reported in this column")}
                                            tooltipType="table"
                                        >
                                            <div className="tw-flex tw-items-center tw-space-x-1">
                                                <div>{props.type === 'approved' ? $t('Approved By') : $t('Rejected By')}</div>
                                                <CircleInfoIcon className="tw-w-3 tw-h-3 tw-text-blue" />
                                            </div>
                                        </Tooltip>
                                    </th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{props.type === 'approved' ? $t('Approved At') : $t('Rejected At')}</th>
                                </>
                            )}
                            <th className="tw-w-56 tw-min-w-56 tw-max-w-56">{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fetchPurchaseRequisitionsRequest.loading ? (
                            <TableRowSkeleton rows={10} columns={9} fixed={true} />
                        ) : (
                            fetchPurchaseRequisitionsRequest.data?.data?.map((pr) => (
                                <tr key={pr.id}>
                                    <td className="tw-font-medium tw-sticky tw-left-0 tw-h-fit tw-text-wrap">
                                        <span className="tw-block tw-font-medium tw-text-wrap">{pr.title}</span>
                                    </td>
                                    <td className="tw-text-wrap">{pr.pr_code}</td>
                                    <td className="tw-truncate">
                                        <Tooltip placement="bottom" tooltipContent={pr.owner_name || ''} tooltipType="text">
                                            <span>{pr.owner_name || ''}</span>
                                        </Tooltip>
                                    </td>
                                    <td className="inbud-numeric-cell tw-text-wrap">{formatFigure(pr.estimated_value)}</td>
                                    <td className="tw-text-wrap">{pr.procurement_policy_method}</td>
                                    <td className="inbud-date-cell tw-text-wrap">{moment(pr.contract_date).format('DD-MM-YYYY')}</td>
                                    {props.type === 'opened' ? (
                                        <>
                                            <td className="tw-truncate">
                                                <Tooltip placement="bottom" tooltipContent={pr.opener_name || ''} tooltipType="text">
                                                    <span>{pr.opener_name || ''}</span>
                                                </Tooltip>
                                            </td>
                                            <td className="tw-whitespace-normal tw-break-all inbud-date-cell">
                                                {pr.opened_at ? moment(pr.opened_at).format('DD-MM-YYYY') : ''}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="tw-truncate">
                                                <Tooltip placement="bottom" tooltipContent={pr.approved_by} tooltipType="text">
                                                    <span>{pr.approved_by}</span>
                                                </Tooltip>
                                            </td>
                                            <td className="tw-whitespace-normal tw-break-all inbud-date-cell">
                                                {pr.approved_at ? moment(pr.approved_at).format('DD-MM-YYYY') : ''}
                                            </td>
                                        </>
                                    )}
                                    <td className="tw-p-4">
                                        {pr.status === props.cancelledStatus ? (
                                            <div className="tw-flex tw-justify-end tw-gap-x-3">
                                                <TextButton variant="primary" onClick={() => viewPR(pr)}>
                                                    <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View')}
                                                </TextButton>
                                            </div>
                                        ) : (
                                            <div className="tw-flex tw-justify-end tw-gap-x-3">
                                                {props.createPurchaseOrderUrl && props.eaEnabled && !pr.has_order && (
                                                    <TextButton variant="primary" onClick={() => createPurchaseOrder(pr.id)}>
                                                        <CirclePlusIcon className="tw-w-3 tw-h-3" /> {$t('EA')}
                                                    </TextButton>
                                                )}
                                                {(pr.status === props.approvalStatuses.rejected || !!pr.has_approvers) && (
                                                    <TextButton onClick={() => editPR(pr)}>
                                                        <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit')}
                                                    </TextButton>
                                                )}
                                                <TextButton variant="primary" onClick={() => viewPR(pr)}>
                                                    <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View')}
                                                </TextButton>
                                                {!pr.purchase_order && (
                                                    <TextButton variant="danger" onClick={() => deletePR(pr)}>
                                                        <TrashIcon className="tw-w-3 tw-h-3" /> {$t('Delete')}
                                                    </TextButton>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <PurchaseRequisitionsEditModal
                modalId={'edit-pr'}
                modalRef={purchaseRequisitionsEditModalRef}
                modalElement={purchaseRequisitionsEditModalElement}
                closeHandler={closeModal}
                successHandler={successHandler}
                pr={updatedPR}
                policies={policies}
                updateUrl={props.updateUrl}
            />
            <ConfirmationModal
                closeHandler={closeModal}
                successHandler={successHandler}
                triggerAction={triggerDeletePR}
                cleanStates={cleanConfirmationModalStates}
                allowAction={Object.keys(deletedPR).length > 0}
                modalId={'delete-pr'}
                modalRef={purchaseRequisitionsDeleteModalRef}
                modalElement={purchaseRequisitionsDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete') + ' ' + [deletedPR?.pr_code]}
                actionCompletedMsg={[deletedPR?.pr_code] + $t('Deleted')}
                actionFailedMsg={[deletedPR?.pr_code] + $t(' could not be deleted')}
                title={$t('Delete Purchase Requisition')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />
            <PurchaseRequisitionsViewModal
                modalId={'view-pr'}
                modalRef={purchaseRequisitionsViewModalRef}
                modalElement={purchaseRequisitionsViewModalElement}
                closeHandler={closeModal}
                successHandler={successHandler}
                pr={viewedPR}
                displayApprovalStatuses={displayApprovalStatuses}
            />
        </div>
    );
}
